import React from 'react';
import { ShoppingCart, Play, Nfc, QrCode, Link2, Wallet, Handshake, BarChart3, Eye, BookUser, Sparkles, Truck, ShieldCheck, CreditCard } from 'lucide-react';
import useProducts from '../hooks/use-products';
import PhoneCard from './mock/PhoneCard';

const chips = [
  [Nfc, 'NFC'],
  [QrCode, 'QR Code'],
  [Link2, 'Link na bio'],
  [Wallet, 'Wallet'],
  [Handshake, 'Connect'],
  [BarChart3, 'CRM e métricas'],
];

const Float = ({ className, children }) => (
  <div className={`absolute z-10 items-center gap-2.5 rounded-2xl bg-white/95 backdrop-blur border border-gray-100 px-3 py-2.5 shadow-[0_18px_40px_-14px_rgba(15,15,16,0.35)] text-left ${className}`}>
    {children}
  </div>
);

const Hero = () => {
  const products = useProducts();
  const minPrice = Math.min(...products.map((p) => Number(p.price)).filter(Number.isFinite));
  const fmt = (v) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
  <section id="top" className="relative pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden">
    <div className="absolute inset-0 -z-10 pointer-events-none">
      {/* brilho de fundo: gradiente radial (parece o desfoque, mas não pesa na hora de desenhar a tela) */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1100px] h-[560px] max-w-none" style={{ background: 'radial-gradient(ellipse 50% 50% at 30% 50%, rgba(47,229,150,0.20), transparent 100%), radial-gradient(ellipse 45% 45% at 70% 50%, rgba(59,167,255,0.16), transparent 100%)' }} />
    </div>

    <div className="max-w-6xl mx-auto px-4 grid lg:grid-cols-[1.05fr_0.95fr] gap-14 lg:gap-8 items-center">
      <div className="text-center lg:text-left">
        <div className="inline-flex items-center gap-2 bg-white border border-[#2FE596]/30 rounded-full px-4 py-1.5 text-xs md:text-sm text-gray-700 shadow-sm">
          <Sparkles className="w-4 h-4 text-[#2FE596]" />
          Cartão físico + painel online
        </div>

        <h1 className="mt-6 text-4xl sm:text-5xl lg:text-[3.6rem] font-extrabold tracking-tight leading-[1.06] text-[#0f0f10]">
          Seja lembrado e feche mais <span className="pp-gradient-text">negócios</span> com um cartão que se atualiza sozinho
        </h1>

        <p className="mt-6 text-gray-600 text-base md:text-lg max-w-xl mx-auto lg:mx-0 leading-relaxed">
          Cartão de visita eletrônico com NFC e QR Code: compartilhe o seu contato por aproximação, QR Code ou link, capte leads com CRM e acompanhe visitas, cliques e contatos salvos em um painel. Sem aplicativo.
        </p>

        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3">
          <a href="#loja" className="pp-btn-gradient inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-base">
            <ShoppingCart className="w-5 h-5" />Escolher meu cartão
          </a>
          <a
            href="https://baseplus.com.br/c/andre"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white border border-gray-200 hover:border-gray-300 text-[#0f0f10] px-7 py-3.5 rounded-full font-semibold text-base transition"
          >
            <Play className="w-4 h-4 text-[#2FE596]" fill="#2FE596" />Ver cartão de exemplo
          </a>
        </div>

        <ul className="mt-5 flex flex-wrap justify-center lg:justify-start gap-x-5 gap-y-2 text-xs sm:text-sm text-gray-600" aria-label="Condições de compra">
          {Number.isFinite(minPrice) && <li className="font-semibold text-[#0f0f10]">A partir de R$ {fmt(minPrice)}</li>}
          <li className="inline-flex items-center gap-1.5"><Truck className="w-4 h-4 text-[#0b6f4a]" />Frete grátis</li>
          <li className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#0b6f4a]" />Garantia contra defeito de fabricação</li>
          <li className="inline-flex items-center gap-1.5"><CreditCard className="w-4 h-4 text-[#0b6f4a]" />Pagamento seguro pelo Mercado Pago</li>
        </ul>

        <ul className="mt-6 flex flex-wrap justify-center lg:justify-start gap-2" aria-label="Formas de compartilhar e recursos">
          {chips.map(([Icon, label]) => (
            <li key={label} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-sm">
              <Icon className="w-3.5 h-3.5 text-[#0b6f4a]" />{label}
            </li>
          ))}
        </ul>
      </div>

      <div className="relative flex justify-center pp-hero-mock">
        <div className="absolute -inset-10 -z-10" aria-hidden="true" style={{ background: 'radial-gradient(circle at 25% 80%, rgba(47,229,150,0.25), transparent 55%), radial-gradient(circle at 75% 20%, rgba(59,167,255,0.25), transparent 55%)' }} />
        <div className="relative">
          <PhoneCard theme="baseplus" name="Mariana Souza" role="Diretora comercial" />

          <Float className="flex -left-9 top-3 sm:-left-28 sm:top-14 scale-[0.82] sm:scale-100 origin-left">
            <span className="w-9 h-9 rounded-xl bg-[#2FE596]/15 flex items-center justify-center"><Handshake className="w-4 h-4 text-[#0b6f4a]" /></span>
            <span><span className="block text-[11px] font-bold text-[#0f0f10] leading-tight">Novo lead</span><span className="block text-[10px] text-gray-500 leading-tight">Fernanda Lopes · agora</span></span>
          </Float>
          <Float className="flex -right-9 top-[352px] sm:-right-28 sm:top-24 scale-[0.82] sm:scale-100 origin-right">
            <span className="w-9 h-9 rounded-xl bg-[#3BA7FF]/15 flex items-center justify-center"><BookUser className="w-4 h-4 text-[#0369a1]" /></span>
            <span><span className="block text-[11px] font-bold text-[#0f0f10] leading-tight">Contato salvo</span><span className="block text-[10px] text-gray-500 leading-tight">na agenda do celular</span></span>
          </Float>
          <Float className="flex -left-9 bottom-[72px] sm:-left-24 sm:bottom-24 scale-[0.82] sm:scale-100 origin-left">
            <span className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center"><Eye className="w-4 h-4 text-violet-700" /></span>
            <span><span className="block text-[11px] font-bold text-[#0f0f10] leading-tight">1.284 visualizações</span><span className="block text-[10px] text-gray-500 leading-tight">41% por NFC</span></span>
          </Float>
          <Float className="flex -right-9 bottom-3 sm:-right-24 sm:bottom-10 scale-[0.82] sm:scale-100 origin-right">
            <span className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center"><Wallet className="w-4 h-4 text-amber-700" /></span>
            <span><span className="block text-[11px] font-bold text-[#0f0f10] leading-tight">Na carteira</span><span className="block text-[10px] text-gray-500 leading-tight">Apple e Google Wallet</span></span>
          </Float>
        </div>
      </div>
    </div>
  </section>
);
};

export default Hero;
